import SignGeneratedDocument from "../features/signatures/SignGeneratedDocument";
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  FaBrain,
  FaCheck,
  FaDownload,
  FaEdit,
  FaEye,
  FaFolderOpen,
  FaLock,
  FaMagic,
  FaPlus,
  FaSave,
  FaSearch,
  FaShareAlt,
  FaTrash,
  FaTimes,
  FaUsers,
  FaThumbtack,
  FaChevronDown,
  FaChevronRight,
} from "react-icons/fa";
import AIDocumentService from "../services/AIDocumentService";
import { generateDocumentPdf } from "../services/documentPdfService";
import { sanitizeDocumentTemplateHtml } from "../security/documentTemplateHtml";
import { getAIDocumentWorkspaceActions } from "./aiDocumentsWorkspaceUtils";

const emptyAcl = {
  accessMode: "private",
  viewerUserIds: [],
  viewerSectorIds: [],
  editorUserIds: [],
  editorSectorIds: [],
};

const normalizeIds = (value) => (Array.isArray(value) ? value.map(Number).filter(Number.isInteger) : []);

const toggleId = (current, id) => {
  const numeric = Number(id);
  return current.includes(numeric) ? current.filter((item) => item !== numeric) : [...current, numeric];
};

const currentDateText = () =>
  new Intl.DateTimeFormat("pt-BR", {
    timeZone: "America/Sao_Paulo",
    day: "2-digit",
    month: "long",
    year: "numeric",
  }).format(new Date());

const replaceCurrentDateTokens = (value) =>
  String(value || "")
    .replace(/\[\s*data\s+atual\s*\]?/gi, currentDateText())
    .replace(/\[\s*data\s+de\s+hoje\s*\]?/gi, currentDateText())
    .replace(/\{\{\s*(?:data[_\s-]*atual|generated[_\s-]*date|current[_\s-]*date)\s*\}\}/gi, currentDateText());

const normalizedDocumentHtml = (value) =>
  sanitizeDocumentTemplateHtml(replaceCurrentDateTokens(value));

const documentField = (title, content) => ({
  id: "documento-ia",
  label: title || "Documento IA",
  documentTemplate: {
    title: title || "Documento IA",
    content: normalizedDocumentHtml(content),
  },
});

function RichDocumentEditor({ value, onChange, disabled }) {
  const editorRef = useRef(null);

  useEffect(() => {
    if (!editorRef.current) return;
    const next = normalizedDocumentHtml(value);
    if (editorRef.current.innerHTML !== next) editorRef.current.innerHTML = next;
  }, [value]);

  const sync = () => {
    if (!editorRef.current) return;
    onChange(editorRef.current.innerHTML);
  };

  const command = (name, commandValue = null) => {
    if (disabled || !editorRef.current) return;
    editorRef.current.focus();
    document.execCommand(name, false, commandValue);
    sync();
  };

  if (disabled) {
    return (
      <div
        className="prose max-w-none min-h-[260px] rounded-2xl border border-slate-200 bg-white p-7 text-slate-800"
        dangerouslySetInnerHTML={{ __html: normalizedDocumentHtml(value) }}
      />
    );
  }

  return (
    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
      <div className="flex flex-wrap items-center gap-1 border-b border-slate-200 bg-slate-50 p-2">
        <select
          aria-label="Formato do parágrafo"
          defaultValue="p"
          onChange={(event) => command("formatBlock", event.target.value)}
          className="rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-xs font-bold text-slate-700"
        >
          <option value="p">Texto</option>
          <option value="h2">Título</option>
          <option value="h3">Subtítulo</option>
        </select>
        {[
          ["bold", "B", "Negrito"],
          ["italic", "I", "Itálico"],
          ["underline", "U", "Sublinhado"],
          ["insertUnorderedList", "• Lista", "Lista com marcadores"],
          ["insertOrderedList", "1. Lista", "Lista numerada"],
          ["justifyLeft", "⇤", "Alinhar à esquerda"],
          ["justifyCenter", "↔", "Centralizar"],
          ["justifyRight", "⇥", "Alinhar à direita"],
          ["undo", "↶", "Desfazer"],
          ["redo", "↷", "Refazer"],
        ].map(([cmd, label, aria]) => (
          <button
            key={cmd}
            type="button"
            title={aria}
            aria-label={aria}
            onMouseDown={(event) => {
              event.preventDefault();
              command(cmd);
            }}
            className="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-extrabold text-slate-700 transition hover:border-blue-300 hover:text-blue-700"
          >
            {label}
          </button>
        ))}
      </div>
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onInput={sync}
        data-placeholder="Comece a escrever seu documento..."
        className="prose min-h-[420px] max-w-none p-7 text-sm leading-relaxed text-slate-800 outline-none focus:bg-blue-50/10 empty:before:text-slate-300 empty:before:content-[attr(data-placeholder)]"
      />
    </div>
  );
}

function AccessPicker({ title, items, selected, onChange, icon: Icon = FaUsers }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4">
      <div className="mb-3 flex items-center gap-2 text-sm font-extrabold text-slate-800">
        <Icon className="text-blue-600" /> {title}
      </div>
      <div className="max-h-44 space-y-2 overflow-auto pr-1">
        {items.length ? items.map((item) => (
          <label key={item.id} className="flex cursor-pointer items-center gap-3 rounded-xl px-3 py-2 hover:bg-slate-50">
            <input
              type="checkbox"
              checked={selected.includes(Number(item.id))}
              onChange={() => onChange(toggleId(selected, item.id))}
              className="h-4 w-4 rounded border-slate-300 text-blue-600"
            />
            <span className="min-w-0">
              <span className="block truncate text-sm font-bold text-slate-800">{item.name}</span>
              {item.email && <span className="block truncate text-xs text-slate-400">{item.email}</span>}
            </span>
          </label>
        )) : <p className="text-sm text-slate-400">Nenhuma opção disponível.</p>}
      </div>
    </div>
  );
}

export default function AIDocuments() {
  const [documents, setDocuments] = useState([]);
  const [accessOptions, setAccessOptions] = useState({ users: [], sectors: [] });
  const [preset, setPreset] = useState(emptyAcl);
  const [selectedId, setSelectedId] = useState(null);
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("Geral");
  const [prompt, setPrompt] = useState("");
  const [content, setContent] = useState("");
  const [acl, setAcl] = useState(emptyAcl);
  const [search, setSearch] = useState("");
  const [saveOpen, setSaveOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [saveAsDefault, setSaveAsDefault] = useState(false);
  const [busy, setBusy] = useState("");
  const [feedback, setFeedback] = useState("");
  const [error, setError] = useState("");
  const [expandedFolders, setExpandedFolders] = useState({});

  const load = async () => {
    try {
      const [docs, options, savedPreset] = await Promise.all([
        AIDocumentService.list(),
        AIDocumentService.accessOptions(),
        AIDocumentService.preset(),
      ]);
      setDocuments(Array.isArray(docs) ? docs : []);
      setAccessOptions({
        users: Array.isArray(options?.users) ? options.users : [],
        sectors: Array.isArray(options?.sectors) ? options.sectors : [],
      });
      const normalizedPreset = { ...emptyAcl, ...(savedPreset || {}) };
      normalizedPreset.viewerUserIds = normalizeIds(normalizedPreset.viewerUserIds);
      normalizedPreset.viewerSectorIds = normalizeIds(normalizedPreset.viewerSectorIds);
      normalizedPreset.editorUserIds = normalizeIds(normalizedPreset.editorUserIds);
      normalizedPreset.editorSectorIds = normalizeIds(normalizedPreset.editorSectorIds);
      setPreset(normalizedPreset);
      setAcl(normalizedPreset);
    } catch (err) {
      setError(err?.response?.data?.error || "Não foi possível carregar Documentos IA.");
    }
  };

  useEffect(() => { load(); }, []);

  const visibleDocuments = useMemo(() => {
    const term = search.trim().toLocaleLowerCase("pt-BR");
    if (!term) return documents;
    return documents.filter((item) =>
      [item.title, item.category, item.createdBy?.name]
        .filter(Boolean)
        .join(" ")
        .toLocaleLowerCase("pt-BR")
        .includes(term)
    );
  }, [documents, search]);

  const pinnedDocuments = useMemo(
    () => visibleDocuments.filter((item) => item.pinned),
    [visibleDocuments],
  );

  const documentFolders = useMemo(() => {
    const grouped = new Map();
    visibleDocuments.forEach((item) => {
      const name = item.category || "Geral";
      if (!grouped.has(name)) grouped.set(name, []);
      grouped.get(name).push(item);
    });
    return Array.from(grouped.entries())
      .map(([name, items]) => ({
        name,
        items: [...items].sort((a, b) => String(a.title || "").localeCompare(String(b.title || ""), "pt-BR")),
      }))
      .sort((a, b) => a.name.localeCompare(b.name, "pt-BR"));
  }, [visibleDocuments]);

  const reset = () => {
    setSelectedId(null);
    setTitle("");
    setCategory("Geral");
    setPrompt("");
    setContent("");
    setAcl(preset);
    setSaveOpen(false);
    setPreviewOpen(false);
    setSaveAsDefault(false);
    setFeedback("");
    setError("");
  };

  const openDocument = (item) => {
    setSelectedId(item.id);
    setTitle(item.title || "");
    setCategory(item.category || "Geral");
    setPrompt(item.prompt || "");
    setContent(normalizedDocumentHtml(item.content || ""));
    setAcl({
      accessMode: item.accessMode || "private",
      viewerUserIds: normalizeIds(item.viewerUserIds),
      viewerSectorIds: normalizeIds(item.viewerSectorIds),
      editorUserIds: normalizeIds(item.editorUserIds),
      editorSectorIds: normalizeIds(item.editorSectorIds),
    });
    setSaveOpen(false);
    setPreviewOpen(false);
    setFeedback("");
    setError("");
  };

  const generate = async () => {
    if (!prompt.trim()) {
      setError("Descreva o documento que deseja criar.");
      return;
    }
    setBusy("generate");
    setError("");
    setFeedback("");
    try {
      const generated = await AIDocumentService.generate({ prompt, title, category });
      setTitle(generated.title || title);
      setCategory(generated.category || category || "Geral");
      setContent(normalizedDocumentHtml(generated.content || ""));
      setFeedback("Documento gerado pela Nash IA. Revise o conteúdo antes de salvar.");
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || "Não foi possível gerar o documento.");
    } finally {
      setBusy("");
    }
  };

  const buildPdfBlob = async () => {
    const result = await generateDocumentPdf({
      field: documentField(title, content),
      fields: [],
      values: {},
      instance: { id: selectedId || "rascunho" },
      save: false,
      outputOnly: true,
    });
    const blob = result?.blob || result?.doc?.output?.("blob");
    if (!blob) throw new Error("Não foi possível preparar o PDF para salvamento.");
    return blob;
  };

  const downloadPdf = async () => {
    if (!content.trim()) return;
    setBusy("pdf");
    try {
      await generateDocumentPdf({
        field: documentField(title, content),
        fields: [],
        values: {},
        instance: { id: selectedId || "rascunho" },
      });
    } catch (err) {
      setError(err?.message || "Não foi possível gerar o PDF.");
    } finally {
      setBusy("");
    }
  };

  const save = async () => {
    const normalizedContent = normalizedDocumentHtml(content);
    if (!normalizedContent.trim()) {
      setError("Gere ou escreva o conteúdo antes de salvar.");
      return;
    }
    setBusy("save");
    setError("");
    setFeedback("");
    try {
      const existing = documents.find((item) => item.id === selectedId);
      const basePayload = {
        title: title.trim() || "Documento sem título",
        category: category.trim() || "Geral",
        prompt,
        content: normalizedContent,
        ...acl,
        folderId: existing?.folderId || null,
        filePath: existing?.filePath || null,
        saveAsDefault,
      };

      let saved = selectedId
        ? await AIDocumentService.update(selectedId, basePayload)
        : await AIDocumentService.create(basePayload);

      let pdfWarning = "";
      try {
        const preparedFolder = await AIDocumentService.prepareFolder(saved.id);
        const blob = await buildPdfBlob();
        const upload = await AIDocumentService.uploadPdf({
          blob,
          id: saved.id,
          title: basePayload.title,
          category: basePayload.category,
        });
        const uploadedFolderId = Number(upload?.folder?.id);
        const preparedFolderId = Number(preparedFolder?.id);
        const folderId = Number.isInteger(uploadedFolderId)
          ? uploadedFolderId
          : (Number.isInteger(preparedFolderId) ? preparedFolderId : null);
        saved = await AIDocumentService.update(saved.id, {
          ...basePayload,
          folderId,
          filePath: upload?.relativePath || null,
          saveAsDefault: false,
        });
      } catch (pdfError) {
        pdfWarning = pdfError?.response?.data?.error || pdfError?.message || "não foi possível gerar o PDF";
      }

      if (saveAsDefault) setPreset({ ...acl });
      setSelectedId(saved.id);
      setContent(normalizedContent);
      setAcl({
        accessMode: saved.accessMode || "private",
        viewerUserIds: normalizeIds(saved.viewerUserIds),
        viewerSectorIds: normalizeIds(saved.viewerSectorIds),
        editorUserIds: normalizeIds(saved.editorUserIds),
        editorSectorIds: normalizeIds(saved.editorSectorIds),
      });
      setSaveOpen(false);
      setSaveAsDefault(false);
      await load();
      setFeedback(
        pdfWarning
          ? `Documento salvo. O conteúdo ficou disponível em Documentos IA, mas houve uma falha ao atualizar o PDF no Gerenciador de Arquivos: ${pdfWarning}`
          : "Documento salvo e organizado no Gerenciador de Arquivos."
      );
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || "Não foi possível salvar o documento.");
    } finally {
      setBusy("");
    }
  };

  const remove = async (item) => {
    if (!window.confirm('Mover "' + item.title + '" da lista de Documentos IA? O arquivo salvo continuará sujeito às regras do Gerenciador de Arquivos.')) return;
    setBusy("delete");
    try {
      await AIDocumentService.remove(item.id);
      if (selectedId === item.id) reset();
      await load();
    } catch (err) {
      setError(err?.response?.data?.error || "Não foi possível excluir o documento.");
    } finally {
      setBusy("");
    }
  };

  const togglePin = async (item) => {
    if (!item?.id) return;
    setError("");
    try {
      if (item.pinned) await AIDocumentService.unpin(item.id);
      else await AIDocumentService.pin(item.id);
      setDocuments((current) =>
        current.map((document) =>
          document.id === item.id ? { ...document, pinned: !item.pinned } : document
        )
      );
    } catch (err) {
      setError(err?.response?.data?.error || "Não foi possível alterar o documento fixado.");
    }
  };

  const toggleFolder = (name) => {
    setExpandedFolders((current) => ({ ...current, [name]: current[name] === false }));
  };

  const canEditSelected = !selectedId || documents.find((item) => item.id === selectedId)?.canEdit !== false;
  const workspaceActions = useMemo(
    () => getAIDocumentWorkspaceActions(content, canEditSelected),
    [content, canEditSelected],
  );

  return (
    <div className="min-h-full bg-slate-50 p-4 md:p-7" style={{ fontFamily: "'Montserrat', sans-serif" }}>
      <div className="mx-auto max-w-[1600px] space-y-5">
        <header className="overflow-hidden rounded-[30px] bg-[#071f54] p-7 text-white shadow-xl md:p-9">
          <div className="flex flex-col gap-6 xl:flex-row xl:items-center xl:justify-between">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-extrabold uppercase tracking-[0.16em] text-sky-100">
                <FaBrain /> Nash IA
              </span>
              <h1 className="mt-3 text-[1.45rem] font-black leading-tight">Documentos IA</h1>
              <p className="mt-2 max-w-3xl text-sm leading-relaxed text-blue-100 md:text-base">
                Crie documentos profissionais com os dados cadastrais da empresa e o mesmo padrão institucional dos processos.
              </p>
            </div>
            <button
              type="button"
              onClick={reset}
              className="inline-flex items-center justify-center gap-2 rounded-full bg-white px-5 py-3 text-sm font-extrabold text-[#092c78] shadow-lg"
            >
              <FaPlus /> Novo documento
            </button>
          </div>
        </header>

        {feedback && <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-sm font-bold text-emerald-700">{feedback}</div>}
        {error && <div className="rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm font-bold text-rose-700">{error}</div>}

        <div className="grid gap-5 xl:grid-cols-[330px_minmax(0,1fr)]">
          <aside className="rounded-[26px] border border-slate-200 bg-white p-4 shadow-sm">
            <div className="relative">
              <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Buscar documentos"
                className="w-full rounded-2xl border border-slate-200 bg-slate-50 py-3 pl-11 pr-4 text-sm outline-none focus:border-blue-400"
              />
            </div>
            <div className="mt-4 max-h-[720px] space-y-5 overflow-auto pr-1">
              <section>
                <div className="mb-2 flex items-center justify-between px-1">
                  <h3 className="text-xs font-black uppercase tracking-[0.12em] text-slate-500">Fixados</h3>
                  <FaThumbtack className="text-xs text-blue-500" />
                </div>
                <div className="space-y-2">
                  {pinnedDocuments.map((item) => (
                    <div
                      key={"pin-" + item.id}
                      className={"group rounded-2xl border p-3 transition " + (selectedId === item.id ? "border-blue-300 bg-blue-50" : "border-slate-200 hover:border-slate-300")}
                    >
                      <div className="flex items-start gap-2">
                        <button type="button" onClick={() => openDocument(item)} className="min-w-0 flex-1 text-left">
                          <span className="block truncate text-sm font-extrabold text-slate-900">{item.title}</span>
                          <span className="mt-1 block text-xs font-semibold text-slate-400">{item.category}</span>
                        </button>
                        <button type="button" onClick={() => togglePin(item)} title="Desafixar" className="rounded-lg p-2 text-blue-600 hover:bg-blue-100">
                          <FaThumbtack />
                        </button>
                      </div>
                    </div>
                  ))}
                  {!pinnedDocuments.length && <p className="rounded-xl bg-slate-50 px-3 py-4 text-center text-xs text-slate-400">Fixe os documentos que usa com frequência.</p>}
                </div>
              </section>

              <section>
                <div className="mb-2 flex items-center gap-2 px-1">
                  <FaFolderOpen className="text-blue-600" />
                  <h3 className="text-xs font-black uppercase tracking-[0.12em] text-slate-500">Pastas</h3>
                </div>
                <div className="mb-3 flex flex-wrap items-center justify-between gap-2 text-xs text-slate-500">
                  <span>{documentFolders.length} pastas · {visibleDocuments.length} documentos</span>
                  <div className="flex gap-3">
                    <button type="button" className="font-semibold text-blue-900" onClick={() => setExpandedFolders(Object.fromEntries(documentFolders.map(folder => [folder.name, true])))}>Expandir</button>
                    <button type="button" className="font-semibold text-blue-900" onClick={() => setExpandedFolders(Object.fromEntries(documentFolders.map(folder => [folder.name, false])))}>Recolher</button>
                  </div>
                </div>
                <div className="max-h-[70vh] space-y-2 overflow-y-auto pr-1">
                  {documentFolders.map((folder) => {
                    const isOpen = expandedFolders[folder.name] !== false || Boolean(search.trim());
                    return (
                      <div key={folder.name} className="overflow-hidden rounded-2xl border border-slate-200">
                        <button
                          type="button"
                          aria-expanded={isOpen}
                          onClick={() => toggleFolder(folder.name)}
                          className="flex w-full items-center gap-2 bg-slate-50 px-3 py-3 text-left text-sm font-extrabold text-slate-800 hover:bg-slate-100"
                        >
                          {isOpen ? <FaChevronDown className="text-xs text-slate-400" /> : <FaChevronRight className="text-xs text-slate-400" />}
                          <FaFolderOpen className="text-blue-600" />
                          <span className="min-w-0 flex-1 truncate">{folder.name}</span>
                          <span className="rounded-full bg-white px-2 py-0.5 text-[10px] text-slate-400">{folder.items.length}</span>
                        </button>
                        {isOpen && (
                          <div className="divide-y divide-slate-100 bg-white">
                            {folder.items.map((item) => (
                              <div key={"folder-" + item.id} className={selectedId === item.id ? "bg-blue-50" : ""}>
                                <div className="flex items-center gap-1 px-3 py-2">
                                  <button type="button" onClick={() => openDocument(item)} className="min-w-0 flex-1 text-left">
                                    <span className="block truncate text-sm font-bold text-slate-700">{item.title}</span>
                                    <span className="block text-[11px] text-slate-400">{new Date(item.updatedAt).toLocaleDateString("pt-BR")}</span>
                                  </button>
                                  <button type="button" onClick={() => togglePin(item)} title={item.pinned ? "Desafixar" : "Fixar"} className={"rounded-lg p-2 " + (item.pinned ? "text-blue-600" : "text-slate-300 hover:text-blue-600")}>
                                    <FaThumbtack />
                                  </button>
                                  {item.canEdit && (
                                    <button type="button" onClick={() => remove(item)} title="Excluir" className="rounded-lg p-2 text-slate-300 hover:bg-rose-50 hover:text-rose-500">
                                      <FaTrash />
                                    </button>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {!documentFolders.length && <p className="p-6 text-center text-sm text-slate-400">Nenhum documento salvo.</p>}
                </div>
              </section>
            </div>
          </aside>

          <main className="space-y-5">
            <div className="sticky top-3 z-30 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-blue-100 bg-white/95 px-4 py-3 shadow-lg backdrop-blur">
              <div className="min-w-0">
                <span className="block truncate text-sm font-black text-slate-900">{title.trim() || "Novo documento"}</span>
                <span className="text-xs font-semibold text-slate-400">{selectedId ? "Documento salvo" : "Rascunho"}</span>
              </div>
              <div className="flex flex-wrap gap-2">
                <SignGeneratedDocument onSaveSigned={selectedId && canEditSelected ? async (blob) => {
                  const original = documents.find(item => item.id === selectedId);
                  if (!original?.canEdit) throw new Error("Você não tem permissão para salvar neste documento.");
                  if (!original.folderId) await AIDocumentService.prepareFolder(original.id);
                  const uploaded = await AIDocumentService.uploadPdf({ blob, id: original.id, title: original.title, category: original.category });
                  if (original.folderId && Number(uploaded?.folder?.id) !== Number(original.folderId)) throw new Error("A pasta do documento mudou. Reabra o documento antes de salvar.");
                  const saved = await AIDocumentService.update(original.id, { ...original, content: normalizedDocumentHtml(content), filePath: uploaded.relativePath, folderId: uploaded.folder?.id || original.folderId, saveAsDefault: false });
                  setDocuments(current => current.map(item => item.id === saved.id ? { ...item, ...saved } : item));
                  setFeedback("PDF assinado salvo com o mesmo nome e na pasta original. O histórico permanece no módulo Assinaturas.");
                } : undefined} disabled={!content.trim() || Boolean(busy)} className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-extrabold text-slate-700" prepareFile={async () => new File([await buildPdfBlob()], `${title.trim() || "Documento IA"}.pdf`, { type: "application/pdf" })} />
                <button
                  type="button"
                  onClick={downloadPdf}
                  disabled={!workspaceActions.previewEnabled || busy === "pdf"}
                  className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-extrabold text-slate-700 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <FaDownload /> PDF
                </button>
                {canEditSelected && (
                  <button
                    type="button"
                    onClick={() => setSaveOpen(true)}
                    disabled={!workspaceActions.saveEnabled || busy === "save"}
                    className="inline-flex items-center gap-2 rounded-full bg-[#0b63ce] px-5 py-2 text-sm font-extrabold text-white shadow-md disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    <FaSave /> Salvar documento
                  </button>
                )}
              </div>
            </div>

            <section className="rounded-[26px] border border-slate-200 bg-white p-5 shadow-sm md:p-7">
              <div className="grid gap-4 md:grid-cols-[1fr_220px]">
                <label className="space-y-2">
                  <span className="text-xs font-extrabold uppercase tracking-wide text-slate-500">Título</span>
                  <input
                    value={title}
                    onChange={(event) => setTitle(event.target.value)}
                    disabled={!canEditSelected}
                    placeholder="A IA pode sugerir automaticamente"
                    className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-blue-400 disabled:bg-slate-50"
                  />
                </label>
                <label className="space-y-2">
                  <span className="text-xs font-extrabold uppercase tracking-wide text-slate-500">Categoria</span>
                  <input
                    value={category}
                    onChange={(event) => setCategory(event.target.value)}
                    disabled={!canEditSelected}
                    className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-blue-400 disabled:bg-slate-50"
                  />
                </label>
              </div>

              <label className="mt-4 block space-y-2">
                <span className="text-xs font-extrabold uppercase tracking-wide text-slate-500">O que a Nash IA deve criar?</span>
                <textarea
                  value={prompt}
                  onChange={(event) => setPrompt(event.target.value)}
                  disabled={!canEditSelected}
                  rows={4}
                  placeholder="Ex.: Elabore uma proposta comercial para..., usando os dados cadastrais da empresa quando necessário."
                  className="w-full resize-y rounded-2xl border border-slate-200 px-4 py-3 text-sm leading-relaxed outline-none focus:border-blue-400 disabled:bg-slate-50"
                />
              </label>

              <div className="mt-4 flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={generate}
                  disabled={!canEditSelected || busy === "generate"}
                  className="inline-flex items-center gap-2 rounded-full bg-[#0b63ce] px-5 py-2.5 text-sm font-extrabold text-white shadow-md disabled:opacity-60"
                >
                  <FaMagic /> {busy === "generate" ? "Gerando..." : content ? "Reescrever com IA" : "Gerar com IA"}
                </button>
                <span className="text-xs font-semibold text-slate-400">Opcional — você também pode escrever diretamente no editor abaixo.</span>
              </div>
            </section>

            <section className="rounded-[26px] border border-slate-200 bg-white p-5 shadow-sm md:p-7">
              <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <FaEdit className="text-blue-500" />
                  <div>
                    <h2 className="text-lg font-black text-slate-900">Editar documento</h2>
                    <p className="text-xs font-semibold text-slate-400">Escreva do zero ou use a IA somente quando quiser.</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setPreviewOpen(true)}
                  disabled={!workspaceActions.previewEnabled}
                  className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-blue-50 px-4 py-2 text-sm font-extrabold text-blue-700 disabled:opacity-40"
                >
                  <FaEye /> Prévia
                </button>
              </div>
              <RichDocumentEditor value={content} onChange={setContent} disabled={!canEditSelected} />
            </section>

            {previewOpen && (
              <div className="fixed inset-0 z-[10020] flex items-center justify-center bg-slate-950/55 p-4 backdrop-blur-sm">
                <section className="max-h-[92vh] w-full max-w-5xl overflow-hidden rounded-[28px] bg-white shadow-2xl">
                  <header className="flex items-center justify-between gap-3 border-b border-slate-200 px-5 py-4">
                    <div>
                      <h2 className="text-lg font-black text-slate-900">Prévia</h2>
                      <p className="text-xs font-semibold text-slate-400">{title.trim() || "Documento sem título"}</p>
                    </div>
                    <button type="button" onClick={() => setPreviewOpen(false)} className="rounded-full border border-slate-200 p-2.5 text-slate-500 hover:bg-slate-50" aria-label="Fechar prévia">
                      <FaTimes />
                    </button>
                  </header>
                  <div className="max-h-[calc(92vh-74px)] overflow-y-auto bg-slate-100 p-4 md:p-8">
                    <div className="mx-auto min-h-[840px] max-w-[794px] bg-white p-10 shadow-lg md:p-14">
                      <div className="prose max-w-none text-slate-800" dangerouslySetInnerHTML={{ __html: normalizedDocumentHtml(content) }} />
                    </div>
                  </div>
                </section>
              </div>
            )}

            {saveOpen && content && (
              <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-slate-950/50 p-4 backdrop-blur-sm">
                <section className="max-h-[90vh] w-full max-w-5xl overflow-y-auto rounded-[26px] border border-blue-100 bg-white p-5 shadow-2xl md:p-7">
                <div className="flex items-start gap-3">
                  <span className="rounded-xl bg-blue-50 p-3 text-blue-700"><FaShareAlt /></span>
                  <div>
                    <h2 className="text-lg font-black text-slate-900">Salvar e compartilhar</h2>
                    <p className="text-sm text-slate-500">Estas opções aparecem somente durante o salvamento.</p>
                  </div>
                </div>

                <div className="mt-5 grid gap-3 md:grid-cols-3">
                  {[
                    ["all", "Todos", "Todos os usuários podem visualizar.", FaUsers],
                    ["private", "Ninguém", "Somente você poderá visualizar e editar.", FaLock],
                    ["restricted", "Personalizado", "Escolha usuários e setores.", FaShareAlt],
                  ].map(([value, label, description, Icon]) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setAcl((prev) => ({ ...prev, accessMode: value }))}
                      className={"rounded-2xl border p-4 text-left transition " + (acl.accessMode === value ? "border-blue-400 bg-blue-50" : "border-slate-200")}
                    >
                      <Icon className="text-blue-600" />
                      <span className="mt-3 block text-sm font-extrabold text-slate-900">{label}</span>
                      <span className="mt-1 block text-xs leading-relaxed text-slate-500">{description}</span>
                    </button>
                  ))}
                </div>

                {acl.accessMode !== "private" && (
                  <div className="mt-5 space-y-5">
                    {acl.accessMode === "restricted" && (
                      <div>
                        <h3 className="mb-3 text-sm font-black text-slate-900">Quem pode visualizar</h3>
                        <div className="grid gap-3 md:grid-cols-2">
                          <AccessPicker title="Usuários" items={accessOptions.users} selected={acl.viewerUserIds} onChange={(value) => setAcl((prev) => ({ ...prev, viewerUserIds: value }))} />
                          <AccessPicker title="Setores" items={accessOptions.sectors} selected={acl.viewerSectorIds} onChange={(value) => setAcl((prev) => ({ ...prev, viewerSectorIds: value }))} />
                        </div>
                      </div>
                    )}
                    <div>
                      <h3 className="mb-1 text-sm font-black text-slate-900">Quem pode editar</h3>
                      <p className="mb-3 text-xs text-slate-500">
                        {acl.accessMode === "all" ? "Todos visualizam; somente os selecionados abaixo podem editar." : "Entre quem pode visualizar, escolha quem também pode editar."}
                      </p>
                      <div className="grid gap-3 md:grid-cols-2">
                        <AccessPicker title="Usuários editores" items={accessOptions.users} selected={acl.editorUserIds} onChange={(value) => setAcl((prev) => ({ ...prev, editorUserIds: value }))} icon={FaEdit} />
                        <AccessPicker title="Setores editores" items={accessOptions.sectors} selected={acl.editorSectorIds} onChange={(value) => setAcl((prev) => ({ ...prev, editorSectorIds: value }))} icon={FaEdit} />
                      </div>
                    </div>
                  </div>
                )}

                <label className="mt-5 flex cursor-pointer items-center gap-3 rounded-2xl bg-slate-50 p-4">
                  <input type="checkbox" checked={saveAsDefault} onChange={(event) => setSaveAsDefault(event.target.checked)} className="h-4 w-4 rounded border-slate-300 text-blue-600" />
                  <span>
                    <span className="block text-sm font-extrabold text-slate-800">Salvar como minha configuração padrão</span>
                    <span className="block text-xs text-slate-500">Os próximos documentos já abrirão com esta mesma regra de compartilhamento.</span>
                  </span>
                </label>

                <div className="mt-5 flex flex-wrap justify-end gap-2">
                  <button type="button" onClick={() => setSaveOpen(false)} className="rounded-full border border-slate-200 px-5 py-2.5 text-sm font-bold text-slate-600">Cancelar</button>
                  <button type="button" onClick={save} disabled={busy === "save"} className="inline-flex items-center gap-2 rounded-full bg-[#0b63ce] px-6 py-2.5 text-sm font-extrabold text-white disabled:opacity-60">
                    {busy === "save" ? <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" /> : <FaCheck />}
                    {busy === "save" ? "Salvando..." : "Salvar documento"}
                  </button>
                </div>
                </section>
              </div>
            )}

          </main>
        </div>
      </div>
    </div>
  );
}
